import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Menyvalg from './Menyvalg'
import { getNavSections } from './menypunkter'
import { LogoOrdmerke } from '../Logo'
import ErrorBoundary from '../ErrorBoundary'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'

export default function DashboardLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { theme, toggleTheme } = useTheme()
  const { impersonating, endImpersonation, role, appUser, hasPermission } = useAuth()
  // Menyen brukeren ser. Favorittstjerna ved sidetittelen (PageHeader) trenger
  // den også, så den deles gjennom Menyvalg sammen med favorittene.
  const seksjoner = useMemo(() => getNavSections(role, hasPermission), [role, hasPermission])

  // App-shell: <main> er scroll-containeren, ikke <body>. Før var ytterboksen
  // min-h-screen, så body scrollet mens main (overflow-auto uten fast høyde)
  // aldri gjorde det — og main var likevel «scroll-boksen» sticky regnes mot.
  // Resultatet: sticky GUI 2.0-headere (PageHeader) fulgte med ut av bildet.
  // Main deles av alle sider, så scrollposisjonen nullstilles ved sidebytte.
  const mainRef = useRef(null)
  const { pathname } = useLocation()
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0
  }, [pathname])

  return (
    <Menyvalg
      seksjoner={seksjoner}
      bruker={appUser?.appUserId || null}
      aktiv={role !== 'kunde'}
      kanEndre={!impersonating}
    >
      <div className="flex h-dvh overflow-hidden bg-bg">
        {impersonating && (
          impersonating.mode === 'user' ? (
            <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-black text-sm py-2 px-4 flex items-center justify-center gap-3 shadow-md">
              <span className="font-medium">
                👁 Du ser appen som <strong>{impersonating.label}</strong>
                {impersonating.role ? ` (${impersonating.role})` : ''} — kun lesing
              </span>
              <button
                onClick={async () => { await endImpersonation(); window.location.href = '/' }}
                className="px-3 py-1 rounded bg-black/80 text-white text-xs font-semibold hover:bg-black"
              >
                Avslutt «Se som»
              </button>
            </div>
          ) : (
            <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white text-sm py-2 px-4 flex items-center justify-center gap-3 shadow-md">
              <span className="font-medium">
                ⚠ Du er pålogget som <strong>{impersonating.label || impersonating.customerName}</strong> (impersonering — admin: {impersonating.adminEmail})
              </span>
              <button
                onClick={async () => { await endImpersonation(); window.location.href = '/' }}
                className="px-3 py-1 rounded bg-white text-red-700 text-xs font-semibold hover:bg-red-50"
              >
                Avslutt og gå tilbake
              </button>
            </div>
          )
        )}
        <Sidebar
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
        />
        <div className={`flex-1 flex flex-col min-w-0 min-h-0 ${impersonating ? 'pt-9' : ''}`}>
          {/* Mobil topplinje: menyknapp, logoen (samme som i sidemenyen, til forsiden) og temaknapp */}
          <header className="lg:hidden bg-nav border-b border-border px-2 py-1.5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => setMobileOpen(true)} aria-label="Åpne menyen" className="text-text-muted hover:text-text w-11 h-11 grid place-items-center rounded-lg">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
              </button>
              <Link to="/" aria-label="ColliXL Pulse, til forsiden" className="py-2 px-1">
                <LogoOrdmerke tema={theme} className="h-[18px] w-auto block" tittel="ColliXL" />
              </Link>
            </div>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Bytt til lyst tema' : 'Bytt til mørkt tema'}
              className="text-text-muted hover:text-text w-11 h-11 grid place-items-center rounded-lg transition-colors"
            >
              {theme === 'dark' ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
                </svg>
              )}
            </button>
          </header>

          <main ref={mainRef} className="flex-1 min-h-0 p-5 overflow-auto">
            {/* Ruta er nøkkelen: et sidebytte nullstiller en feil, og menyen står */}
            <ErrorBoundary key={pathname}>{children}</ErrorBoundary>
          </main>
        </div>
      </div>
    </Menyvalg>
  )
}
